import React from 'react';
import {   FiClock,  FiZap, FiBarChart2, FiHeart, FiSmile, FiMonitor } from 'react-icons/fi';

const UXBurnout = () => {
  const uxFactors = [
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'Intuitive Interface = Less Training Time',
      description: 'When software is intuitive, teachers don\'t need hours of training. They can start using it immediately. This reduces the stress of learning new systems and allows them to focus on teaching rather than figuring out how the software works.',
      stat: '80% of teachers prefer software that requires minimal training',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Faster Task Completion',
      description: 'Good UX reduces the time it takes to complete tasks. Marking attendance takes seconds, not minutes. Entering grades is quick and simple. Generating reports is a one-click process. Less time on tasks means more time for teaching.',
      stat: 'Good UX can reduce task time by 50-70%',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Reduced Cognitive Load',
      description: 'Poorly designed software requires mental effort to navigate—confusing menus, hidden features, complex workflows. This cognitive load exhausts users. Good UX eliminates this, allowing teachers to focus on their work, not the software.',
      stat: '70% of teachers report cognitive load from complex software',
      color: 'bg-amber-50'
    },
    {
      icon: <FiHeart className="w-6 h-6" />,
      title: 'Less Frustration = Less Burnout',
      description: 'Every time software is difficult to use, it creates frustration. Over time, this frustration accumulates and contributes to burnout. Good UX eliminates frustration, making work more enjoyable.',
      stat: '65% of educators cite software frustration as a burnout factor',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Clear Feedback and Guidance',
      description: 'Good UX provides clear feedback—"Attendance marked," "Grades saved," "Report generated." Users know what happened and what to do next. No uncertainty. No guessing. This reduces anxiety and stress.',
      stat: '90% of users prefer software with clear feedback',
      color: 'bg-purple-50'
    },
    {
      icon: <FiSmile className="w-6 h-6" />,
      title: 'Positive Daily Experience',
      description: 'When teachers use software that is pleasant to use, their daily experience improves. They start the day with a positive interaction rather than frustration. This contributes to overall job satisfaction.',
      stat: '85% of teachers say good software improves their mood at work',
      color: 'bg-blue-50'
    }
  ];

  const burnoutSigns = [
    {
      title: 'Frustration with Technology',
      description: 'Teachers who constantly struggle with software show signs of frustration. They may avoid using the system or complain frequently.'
    },
    {
      title: 'Time Spent on Admin Work',
      description: 'When teachers spend more time on admin tasks than teaching, burnout is likely. Good UX should reduce admin time, not increase it.'
    },
    {
      title: 'Declining Adoption',
      description: 'Teachers who stop using the system are signaling that it\'s not worth the effort. Low adoption leads to incomplete data and frustrated administrators.'
    },
    {
      title: 'Increased Errors',
      description: 'Frustrated users make mistakes—incorrect attendance, wrong grades, missed communications. Errors create more work and more frustration.'
    },
    {
      title: 'Resistance to New Features',
      description: 'When teachers resist learning new features, it\'s often because the system has already overwhelmed them. They can\'t handle more complexity.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Teacher burnout is one of the most serious challenges in education today. When teachers are exhausted, stressed, and overwhelmed, they can't teach effectively. They lose motivation. They disengage. They leave the profession.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The causes of burnout are well-documented—excessive workload, lack of support, administrative burden. <strong className="text-slate-900">But one cause is often overlooked: poor user experience in educational software.</strong>
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        When teachers are forced to use complex, confusing, or frustrating software, it adds to their daily stress. It wastes time. It creates frustration. It contributes to burnout. Here's how great UX reduces educator and staff burnout.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">50-70%</div>
          <div className="text-sm text-slate-500">Task time reduced with good UX</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">65%</div>
          <div className="text-sm text-slate-500">Cite software frustration as burnout factor</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">85%</div>
          <div className="text-sm text-slate-500">Say good software improves mood at work</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How Great UX Prevents Burnout</h2>

      {/* UX Factors */}
      {uxFactors.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Burnout Signs */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Signs That Your Software Is Contributing to Burnout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {burnoutSigns.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Poor UX vs. Great UX</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Poor UX (Burnout)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Complex, confusing software</span></li>
              <li className="flex items-start gap-2">• <span>Tasks take too long</span></li>
              <li className="flex items-start gap-2">• <span>Frequent frustration</span></li>
              <li className="flex items-start gap-2">• <span>No clear feedback</span></li>
              <li className="flex items-start gap-2">• <span>Teachers avoid using it</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Great UX (Truyon)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Simple, intuitive software</span></li>
              <li className="flex items-start gap-2">• <span>Fast task completion</span></li>
              <li className="flex items-start gap-2">• <span>Frustration-free experience</span></li>
              <li className="flex items-start gap-2">• <span>Clear guidance and feedback</span></li>
              <li className="flex items-start gap-2">• <span>Teachers love using it</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Human Cost */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Cost of Poor UX</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Every time a teacher struggles with software, it takes a small toll. Over months and years, these small frustrations accumulate into significant stress. Teachers become exhausted. They feel unsupported. They consider leaving.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          When teachers leave, the institution suffers. Recruitment costs are high. Institutional knowledge is lost. Students lose experienced educators. The cycle of burnout and turnover continues.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Good UX is not just about convenience. It's about supporting your educators and protecting your institution.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Delivers Great UX for Educators</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">😊</div>
            <h4 className="font-bold text-slate-900 text-sm">Simple & Intuitive</h4>
            <p className="text-xs text-slate-600">Teachers start using it immediately</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Tasks</h4>
            <p className="text-xs text-slate-600">Attendance, grades, reports in seconds</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Friendly</h4>
            <p className="text-xs text-slate-600">Work from any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💡</div>
            <h4 className="font-bold text-slate-900 text-sm">Clear Guidance</h4>
            <p className="text-xs text-slate-600">Always know what to do</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Frustration-Free</h4>
            <p className="text-xs text-slate-600">No confusion, no stress</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Support When Needed</h4>
            <p className="text-xs text-slate-600">Help is always available</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Support Your Educators with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Educators are the heart of your institution. Their well-being determines the quality of education your students receive. When teachers are burned out, everyone suffers.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is designed with educators in mind. Simple, intuitive, and frustration-free. Fast task completion, clear guidance, and mobile-friendly access. Reduce burnout. Support your educators. Build a better institution.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for great UX.</strong> Happy educators. Thriving institutions.
        </p>
      </div>
    </>
  );
};

export default UXBurnout;